<template>
  <div class="order-information">
    <div class="buyer-info">
      <div class="more-actions">
        <i class="ti ti-printer mr-1 print-content" @click.prevent="handlePrint"></i>
        <i v-if="item.flag" :class="`ti ti-flag-filled flag ${item.flag}`"></i>
      </div>
      <strong class="name select-all">{{ item.delivery_info.name }}</strong>
      <span v-if="item.delivery_info.line[0]" class="first-line select-all">{{ item.delivery_info.line[0] }}</span>
      <span v-if="item.delivery_info.line[1]" class="second-line select-all">{{ item.delivery_info.line[1] }}</span>
      <span v-if="item.delivery_info.city" class="city select-all" :class="{ 'text-danger': addressDanger }">{{ item.delivery_info.city }}</span>
      <span v-if="item.delivery_info.state" class="state select-all" :class="{ 'text-danger': addressDanger }">{{ item.delivery_info.state }}</span>
      <span v-if="item.delivery_info.zip" class="zip select-all" :class="{ 'text-danger': addressDanger }">{{ item.delivery_info.zip }}</span>
      <span v-if="item.delivery_info.country" class="country select-all" :class="{ 'text-danger': addressDanger }" :data-value="item.delivery_info.country">{{ item.delivery_info.country_label }}</span>
      <span v-if="item.delivery_info.phone" class="phone select-all">{{ item.delivery_info.phone }}</span>
      <span v-if="item.delivery_info.note" class="order-note" v-html="$util.autoLink(item.delivery_info.note)"></span>
      <div class="row-actions break-normal">
        <span v-for="(action, key, index) in item.actions" :key="index" :class="[action.id.replace('.', '-'), action.style || '']">
          <a href="#" :aria-label="action.label" @click.prevent="handleAction(item, action)">
            {{ action.label }}
          </a>
        </span>
      </div>
    </div>
    <div class="shop-name">
      <label>Shop name: </label>
      <span v-if="item.account.url" class="select-all">
        <a :href="item.account.url" target="_blank">{{ item.account.name }}</a>
      </span>
      <span v-else class="select-all">{{ item.account.name }}</span>
      - <span class="select-all">{{ item.user_name }}</span> - [{{ item.platform }}]
    </div>
    <div class="order-id">
      <label>Sale ID: </label>
      <span class="select-all">{{ item.sale_detail.external_identify }}</span>
    </div>
    <div class="sales-purchase">
      <div v-if="parseFloat(item.sale_detail.price) > 0" class="w-100">
        <label>Retail Price: </label>
        <span>
          <strong>{{ item.sale_detail.price }}{{ item.sale_detail.currency }}</strong>
          at
          <span v-html="this.$util.timestampToDateTime(parseInt(item.sale_detail.date_created), false, item.timezone || null)"></span>
        </span>
      </div>
      <div v-else class="w-100">
        <label>Sold on: </label>
        <span v-html="this.$util.timestampToDateTime(parseInt(item.sale_detail.date_created), false, item.timezone || null)"></span>
      </div>
      <div v-if="item.sale_detail?.subtotal > 0" class="mb-0">
        Subtotal: {{ item.sale_detail.subtotal }}<span class="currency text-uppercase">{{ item?.sale_detail?.currency || '-' }}</span>
      </div>
      <div v-if="item.sale_detail?.shipping_fee > 0" class="mb-0">
        Shipping Price: {{ item.sale_detail.shipping_fee }}<span class="currency text-uppercase">{{ item?.sale_detail?.currency || '-' }}</span>
      </div>
      <div v-if="item.sale_detail?.tax_amount > 0" class="mb-0">
        Tax Price: {{ item.sale_detail.tax_amount }}<span class="currency text-uppercase">{{ item?.sale_detail?.currency || '-' }}</span>
      </div>
      <div v-if="item.sale_detail?.discount_amount > 0" class="mb-0">
        Discount Price: {{ item.sale_detail.discount_amount }}<span class="currency text-uppercase">{{ item?.sale_detail?.currency || '-' }}</span>
      </div>
      <div v-if="parseFloat(item?.sale_detail?.latest_ship_date) > 0" class="w-100">
        <label>Latest Ship Date: </label>
        <span v-html="this.$util.timestampToDateTime(parseInt(item.sale_detail.latest_ship_date), false, item.timezone || null)"></span>
      </div>
      <div v-if="parseFloat(item?.sale_detail?.latest_delivery_date) > 0" class="w-100">
        <label>Latest Delivery Date: </label>
        <span v-html="this.$util.timestampToDateTime(parseInt(item.sale_detail.latest_delivery_date), false, item.timezone || null)"></span>
      </div>
      <div v-if="item?.purchase_detail?.date_created > 0" class="w-100">
        <label>Purchase Date: </label>
        <span v-html="this.$util.timestampToDateTime(parseInt(item.purchase_detail.date_created))"></span>
      </div>
      <div v-if="prices[0] && prices[0].price" class="w-100">
        <label>Purchase Price: </label>
        <div class="d-inline-block">
          <template v-for="(object, index) in prices" :key="index">
            <p v-if="object.price > 0" class="mb-0">
              <span v-if="object.base_cost > 0" class="has-tip" data-title="Base Cost">
                <strong>B: {{ object.base_cost }}{{ object.currency }}</strong> -
              </span>
              <span v-if="object.shipping_cost > 0" class="has-tip" data-title="Shipping Cost">
                <strong>S: {{ object.shipping_cost }}{{ object.currency }}</strong> -
              </span>
              <span v-if="object.tax_cost > 0" class="has-tip" data-title="Tax Cost">
                <strong>T: {{ object.tax_cost }}{{ object.currency }}</strong> -
              </span>
              <strong v-if="object.price > 0" class="has-tip" data-title="Total Price">T: {{ object.price }}{{ object.currency }}</strong>
            </p>
          </template>
        </div>
      </div>
    </div>
    <div v-if="shipments?.length > 0" class="shipments mb-1">
      <label class="mb-0">Shipments: </label>
      <i v-if="addedTracking" class="ti ti-circle-check-filled text-success mr-1"></i>
      <i v-if="transported" class="ti ti-circle-check-filled text-primary"></i>
      <ul>
        <template v-for="([key, shipment], index) in shipments" :key="index">
          <li v-if="shipment.number">
            <label class="select-all">{{ shipment.carrier }}</label>
            <span> - </span>
            <a v-if="shipment.attachment" :href="shipment.attachment" target="_blank">{{ shipment.number }}</a>
            <span v-else class="select-all">{{ shipment.number }}</span>
            <i :class="[copiedStates[shipment.number] ? 'ti ti-circle-check text-success' : 'ti ti-copy copy-tracking', 'ml-1']" :title="copiedStates[shipment.number] ? 'Copied!' : 'Copy tracking number'" @click.prevent="copyTrackingNumber(shipment.number)"></i>
            <div v-if="purchaseMeta?.[key]?.verified_weight">Actual Weight: {{ purchaseMeta?.[key]?.verified_weight }}g</div>
          </li>
        </template>
      </ul>
    </div>
    <div v-if="parseInt(item.sale_detail.deadline) > 0" class="deadline">
      <label>Deadline: </label>
      <span v-html="this.$util.timestampToDateTime(parseInt(item.sale_detail.deadline))"></span>
    </div>
    <div class="status d-flex flex-wrap mb-2 align-items-center">
      <label class="m-0">Status:</label>
      <span :class="`mr-2 ${item.status}`">{{ statuses?.[item.status]?.text || item?.status }}</span>
      <span v-if="item.manual" class="manual">Manual</span>
      <span v-if="item.is_sub_order" class="is-sub-order">Sub-Order</span>
      <span v-if="item.sub_order" class="sub-orders" @click.stop.prevent="clickSubOrders">Sub-Orders</span>
    </div>
    <div v-if="item.note" class="local-note mb-2">
      <span>{{ item.note }}</span>
    </div>

    <div v-if="item.purchase.length > 0" class="purchase-detail-wrapper">
      <label @click="tooglePurchaseDetail">Purchase Detail</label>
      <div class="purchase-detail collapse">
        <div class="purchase-id">
          <label>Purchase id: </label>
          <ul>
            <li v-for="(purchase, index) in item.purchase" :key="index">
              <label>{{ purchase.vendor_name }}: </label>
              <a v-if="purchase.url" :href="purchase.url" target="_black">{{ purchase.id }}</a>
              <span v-else class="select-all">{{ purchase.id }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div v-if="item.alerts && item.alerts.length > 0" class="alert alert-danger">
      <ul>
        <template v-for="(alert, index) in item.alerts" :key="index">
          <li v-if="alert.messenger" v-html="`[${this.$util.timestampToDateTime(parseInt(alert.date_created))}] ${alert.messenger}`"></li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import BodyItemRowAction from '@/components/TableList/Cols/BodyItemRowAction.vue'
import print from '@/helper/print.js'
export default {
  extends: BodyItemRowAction,
  data() {
    return {
      copiedStates: {}
    }
  },
  computed: {
    store() {
      return this.config?.store
    },
    statuses() {
      return this.store.getter('extra_attributes.statuses', {})
    },
    transports() {
      return this.store.getter('extra_attributes.transports', {})
    },
    shipments() {
      return Object.entries(this.item?.purchase_detail?.shipments || []).filter(([key, item]) => {
        return item.number
      })
    },
    purchaseMeta() {
      return this.item?.purchase_detail?.meta || {}
    },
    prices() {
      return Object.values(this.item?.purchase_detail?.prices || []).filter((item) => {
        return item.price > 0
      })
    },
    addedTracking() {
      return this.transports?.completed?.id == this.item?.purchase_detail?.added_tracking
    },
    transported() {
      return this.item?.purchase_detail?.more?.shipped || false
    },
    verified() {
      return this.item?.verified
    },
    addressDanger() {
      if (this.item?.delivery_info?.country == 'US' && ['FPO', 'AE', 'AP', 'PR', 'VI', 'HI', 'GU', 'MP', 'AS', 'APO', 'AK'].includes(this.item?.delivery_info?.state)) {
        return true
      }
      return false
    }
  },
  mounted() {
    this.itemClass()
  },
  updated() {
    this.itemClass()
  },
  methods: {
    itemClass() {
      if (!(this.$el instanceof HTMLElement)) return
      let $tr = this.$el.closest('tr')
      if ($tr instanceof HTMLElement) {
        if (this.verified && !$tr.classList.contains('verified')) $tr.classList.add('verified')
        if (!this.verified && $tr.classList.contains('verified')) $tr.classList.remove('verified')
        if (this.transport && !$tr.classList.contains('transport')) $tr.classList.add('transport')
        if (!this.transport && $tr.classList.contains('transport')) $tr.classList.remove('transport')
      }
    },
    clickSubOrders() {
      this.$emitter.emit('Modal', {
        name: 'order.index-sub-order',
        $el: this.$el.closest('tr.item'),
        data: this.row
      })
    },
    tooglePurchaseDetail(e) {
      let $el = e.target
      if (['none', ''].indexOf($el.nextSibling.style.display) != -1) {
        $el.nextSibling.style.display = 'block'
        $el.parentElement.classList.add('show')
      } else {
        $el.nextSibling.style.display = 'none'
        $el.parentElement.classList.remove('show')
      }
    },
    handlePrint() {
      let order = _.cloneDeep(this.item)
      order.id = this.row._id
      order.line_items = this.row.line_items
      print.order(order)
    },
    copyTrackingNumber(number) {
      if (!number) return

      const copyToClipboard = (text) => {
        if (navigator.clipboard && window.isSecureContext) {
          return navigator.clipboard.writeText(text)
        } else {
          let textarea = document.createElement('textarea')
          textarea.value = text
          textarea.style.top = '0'
          textarea.style.left = '0'
          textarea.style.position = 'fixed'
          document.body.appendChild(textarea)
          textarea.focus()
          textarea.select()
          try {
            document.execCommand('copy')
            document.body.removeChild(textarea)
            return Promise.resolve()
          } catch (err) {
            document.body.removeChild(textarea)
            return Promise.reject(err)
          }
        }
      }

      copyToClipboard(number)
        .then(() => {
          this.$set ? this.$set(this.copiedStates, number, true) : (this.copiedStates[number] = true)
          setTimeout(() => {
            this.$set ? this.$set(this.copiedStates, number, false) : (this.copiedStates[number] = false)
          }, 2000)
          this.$util.notify({
            status: 'success',
            text: 'Copied tracking number: ' + number
          })
        })
        .catch(() => {
          this.$util.notify({
            status: 'error',
            text: 'Could not copy tracking number'
          })
        })
    }
  },
  props: {
    config: Object
  }
}
</script>

<style lang="scss">
.table-list.order,
.table-list.sub-order {
  .order-information {
    min-width: 320px;
    max-width: 320px;
    width: 320px;
  }
  .sales-purchase {
    strong {
      text-transform: uppercase;
    }
  }
  .shipments {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    .copy-tracking {
      cursor: pointer;
      opacity: 0.7;
      &:hover {
        opacity: 1;
        color: var(--primary, #007bff);
      }
    }
  }
}
</style>
